// React

// Packages

// Utilities
import { CONSTANTS } from 'utilities/configuration';

// Components

const ActivitySetFieldValues = ({
    tagging,
    choice,
    form,
    id,
    utilities,
    REPORT_ID,
}) => {
    const activity = utilities.activities.get(id);
    const {
        Id: Activity___Id,
        Activity_Type__c,
        Things_To_Do__c,
        Things_To_Do_Description__c,
        Activity_Start_Date__c,
        Activity_End_Date__c,
    } = activity;

    // ///////////////////
    // Activity ID
    // ///////////////////

    form.setValue('Activity___Id', Activity___Id);

    // ///////////////////
    // CHOICE
    // ///////////////////

    form.setValue('Choice', choice);
    form.setValue('Activity_Type__c', Activity_Type__c);

    // ///////////////////
    // DETAILS
    // ///////////////////

    form.setValue('Things_To_Do__c', Things_To_Do__c);
    form.setValue('Things_To_Do_Description__c', Things_To_Do_Description__c);
    form.setValue('Dates', {
        from: Activity_Start_Date__c,
        to: Activity_End_Date__c,
    });

    // Get tagging values for activity
    const activityTags = tagging.activity.getCurrentTags(activity);

    // Get tag fields
    const activityTagFields = tagging.activity.formFields;

    // Create fields for each collection and add the values
    for (const [collectionId, formField] of Object.entries(activityTagFields)) {
        const values = activityTags
            .filter(x => x.Tag__r?.Collection__c === collectionId)
            .map(x => x.Tag__c);
        form.setValue(formField, values);
    }

    // ///////////////////
    // REFLECTION
    // ///////////////////

    const reportDetail = utilities.reportDetails.getFromActivityIdAndReportId(
        Activity___Id,
        REPORT_ID
    );

    const hasReflection = reportDetail?.Description__c;

    form.setValue('Reflection___Id', reportDetail.Id || null);

    if (hasReflection) {
        form.setValue(
            'Reflection___Description__c',
            reportDetail.Description__c
        );
    }

    // ///////////////////
    // METRICS
    // ///////////////////

    // Get metrics (up to three)
    const activityMetrics = utilities.activitySuccessMetrics.getFromActivityId(
        Activity___Id
    );

    // Find primary metric
    const primaryAudienceMetric =
        activityMetrics.find(
            x =>
                x.Priority__c ===
                CONSTANTS.ACTIVITY_SUCCESS_METRICS.PRIORITY_PRIMARY
        ) || null;

    // Find secondary metric
    const secondaryAudienceMetric =
        activityMetrics.find(
            x =>
                x.Priority__c ===
                CONSTANTS.ACTIVITY_SUCCESS_METRICS.PRIORITY_SECONDARY
        ) || null;

    // Find additional metric
    const additionalMetric =
        activityMetrics.find(
            x =>
                x.Priority__c ===
                CONSTANTS.ACTIVITY_SUCCESS_METRICS.PRIORITY_ADDITIONAL
        ) || null;

    // Fill out primary audience metric
    if (primaryAudienceMetric) {
        const {
            Id,
            Geographical_Reach__c,
            Target__c,
            Current_Status__c,
            Gender__c,
            Gender_Other__c,
        } = primaryAudienceMetric;

        form.setValue('Primary__Toggle', true);
        form.setValue('Primary___Gender__c', [
            { selectValue: Gender__c, textValue: Gender_Other__c },
        ]);
        form.setValue('Primary___Geographical_Reach__c', Geographical_Reach__c);
        form.setValue('Primary___Target__c', Target__c);
        form.setValue('Primary___Toggle___Target__c', !!Target__c);
        hasReflection &&
            form.setValue('Primary___Current_Status__c', Current_Status__c);
        // Set primary audience metric id in form values
        form.setValue('Primary___Metric_Id', Id);

        // Get tagging values for primary audience metric
        const metricTags = tagging.primaryMetric.getCurrentTags(
            primaryAudienceMetric
        );

        // Get tag fields
        const metricTagFields = tagging.primaryMetric.formFields;

        // Create fields for each collection and add the values
        for (const [collectionId, formField] of Object.entries(
            metricTagFields
        )) {
            const values = metricTags
                .filter(x => x.Tag__r?.Collection__c === collectionId)
                .map(x => x.Tag__c);
            form.setValue(formField, values);
            form.setValue(`${formField}_Removed`, []);
        }
    }

    // Fill out secondary audience metric
    if (secondaryAudienceMetric) {
        const {
            Id,
            Geographical_Reach__c,
            Target__c,
            Current_Status__c,
            Gender__c,
            Gender_Other__c,
        } = secondaryAudienceMetric;

        form.setValue('Secondary__Toggle', true);
        form.setValue('Secondary___Gender__c', [
            { selectValue: Gender__c, textValue: Gender_Other__c },
        ]);
        form.setValue(
            'Secondary___Geographical_Reach__c',
            Geographical_Reach__c
        );
        form.setValue('Secondary___Target__c', Target__c);
        form.setValue('Secondary___Toggle___Target__c', !!Target__c);
        hasReflection &&
            form.setValue('Secondary___Current_Status__c', Current_Status__c);
        // Set secondary audience metric id in form values
        form.setValue('Secondary___Metric_Id', Id);

        // Get tagging values for secondary audience metric
        const metricTags = tagging.secondaryMetric.getCurrentTags(
            secondaryAudienceMetric
        );

        // Get tag fields
        const metricTagFields = tagging.secondaryMetric.formFields;

        // Create fields for each collection and add the values
        for (const [collectionId, formField] of Object.entries(
            metricTagFields
        )) {
            const values = metricTags
                .filter(x => x.Tag__r?.Collection__c === collectionId)
                .map(x => x.Tag__c);
            form.setValue(formField, values);
            form.setValue(`${formField}_Removed`, []);
        }
    }

    // Fill out additional metric
    if (additionalMetric) {
        const {
            Id,
            Name,
            CurrencyIsoCode,
            Geographical_Reach__c,
            Target__c,
            Current_Status__c,
            Gender__c,
            Gender_Other__c,
            Type__c,
        } = additionalMetric;

        form.setValue('Additional__Toggle', true);
        form.setValue('Additional___Type__c', Type__c);
        form.setValue('Additional___Gender__c', [
            { selectValue: Gender__c, textValue: Gender_Other__c },
        ]);
        form.setValue(
            'Additional___Geographical_Reach__c',
            Geographical_Reach__c
        );
        form.setValue('Additional___Target__c', Target__c);
        form.setValue('Additional___Toggle___Target__c', !!Target__c);
        form.setValue('Additional___Name', Name);
        form.setValue('Additional___CurrencyIsoCode', CurrencyIsoCode);
        hasReflection &&
            form.setValue('Additional___Current_Status__c', Current_Status__c);
        // Set secondary audience metric id in form values
        form.setValue('Additional___Metric_Id', Id);

        // Set tagging based on type
        let taggingBasedOnType;
        if (Type__c === CONSTANTS.ACTIVITY_SUCCESS_METRICS.PEOPLE) {
            taggingBasedOnType = tagging.additionalMetricPeople;
        }
        if (Type__c === CONSTANTS.ACTIVITY_SUCCESS_METRICS.FINANCIAL) {
            taggingBasedOnType = tagging.additionalMetricFinancial;
        }
        if (Type__c === CONSTANTS.ACTIVITY_SUCCESS_METRICS.CUSTOM) {
            taggingBasedOnType = tagging.additionalMetricCustom;
        }

        // Get tagging values for additional metric
        const metricTags = taggingBasedOnType.getCurrentTags(additionalMetric);

        // Get tag fields
        const metricTagFields = taggingBasedOnType.formFields;

        // Create fields for each collection and add the values
        for (const [collectionId, formField] of Object.entries(
            metricTagFields
        )) {
            const values = metricTags
                .filter(x => x.Tag__r?.Collection__c === collectionId)
                .map(x => x.Tag__c);
            form.setValue(formField, values);
            form.setValue(`${formField}_Removed`, []);
        }
    }
};

export default ActivitySetFieldValues;
